import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const POLL_MS = 120000;
const REFRESH_EVENTS = [
  "buzzerklip:support-chat-refresh",
  "buzzerklip:member-chat-refresh",
  "buzzer-klip-campaign-extension-refresh",
];
const EMPTY = {
  announcements: { notifications: [] },
  live_campaigns: { count: 0, campaigns: [] },
  completions: { notifications: [] },
  extensions: { notifications: [] },
  support: { unread_count: 0, unread_tickets: [] },
  chat: { unread_count: 0, thread_count: 0 },
  generated_at: "",
};

const NotificationContext = createContext({ ...EMPTY, refresh: async () => {} });

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState(EMPTY);
  const inFlightRef = useRef(null);
  const lastLoadedAtRef = useRef(0);
  const identity = user && user !== false ? `${user.id}:${user.role}` : "";

  const refresh = useCallback(async ({ force = false } = {}) => {
    if (!identity) {
      setData(EMPTY);
      return null;
    }
    if (!force && typeof document !== "undefined" && document.hidden) return null;
    if (!force && typeof navigator !== "undefined" && navigator.onLine === false) return null;
    if (inFlightRef.current) return inFlightRef.current;

    const request = api.get("/notifications/summary", { timeout: 30000 })
      .then(({ data: next }) => {
        setData({ ...EMPTY, ...(next || {}) });
        lastLoadedAtRef.current = Date.now();
        return next;
      })
      .catch(() => null)
      .finally(() => {
        inFlightRef.current = null;
      });
    inFlightRef.current = request;
    return request;
  }, [identity]);

  useEffect(() => {
    if (!identity) {
      setData(EMPTY);
      return undefined;
    }

    let stopped = false;
    let timer;
    const schedule = () => {
      if (stopped) return;
      window.clearTimeout(timer);
      const jitter = Math.floor(Math.random() * 15000);
      timer = window.setTimeout(async () => {
        await refresh();
        schedule();
      }, POLL_MS + jitter);
    };
    const refreshIfStale = () => {
      if (document.hidden || navigator.onLine === false) return;
      if (Date.now() - lastLoadedAtRef.current >= 30000) {
        window.clearTimeout(timer);
        refresh({ force: true }).finally(schedule);
      }
    };

    setData(EMPTY);
    refresh({ force: true }).finally(schedule);
    document.addEventListener("visibilitychange", refreshIfStale);
    window.addEventListener("online", refreshIfStale);
    REFRESH_EVENTS.forEach((eventName) => window.addEventListener(eventName, refreshIfStale));
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", refreshIfStale);
      window.removeEventListener("online", refreshIfStale);
      REFRESH_EVENTS.forEach((eventName) => window.removeEventListener(eventName, refreshIfStale));
    };
  }, [identity, refresh]);

  return (
    <NotificationContext.Provider value={{ ...data, refresh }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotificationCenter = () => useContext(NotificationContext);
